import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaEye, FaEdit, FaTrashAlt } from 'react-icons/fa';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import { UserContext } from '../../UserContext';
import styles from './ListaUsinas.module.css';
import styles_global from '../css/global.module.css';
import styles_content from '../css/main-content.module.css';
import Pagination from '../pagination/Pagination';
import { somenteNumeros } from '../../util/validacoes';
import { useHttpClient } from '../hooks/useHttpClient';

function ListaUsinas() {
    const { user, setUser } = useContext(UserContext);
    const { get, del } = useHttpClient();
    const [usinas, setUsinas] = useState([]);
    const [filtros, setFiltros] = useState({ nome: '', cnpj: '' });
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const jaCarregou = useRef(false);

    useEffect(() => {
        const userData = localStorage.getItem('userData');
        if (userData) setUser(JSON.parse(userData));
    }, [setUser]);

    const fetchUsinas = useCallback(async (usarFiltros = false, page = currentPage) => {
        setLoading(true);
        try {
            const idEmpresa = user?.empresa?.id;
            if (!idEmpresa) throw new Error('Empresa não encontrada.');

            const query = new URLSearchParams({
                cnpj: usarFiltros ? filtros.cnpj || '' : '',
                nome: usarFiltros ? filtros.nome || '' : '',
                idEmpresa: idEmpresa.toString(),
                page: page.toString(),
                pageSize: pageSize.toString()
            }).toString();

            const data = await get(`/hello-sol/usinas?${query}`);
            setUsinas(data.content || []);
            setTotalPages(data.totalPages || 0);
        } catch {
            // erro tratado no hook
        } finally {
            setLoading(false);
        }
    }, [user, filtros, pageSize, get]);

    useEffect(() => {
        if (!jaCarregou.current && user?.empresa?.id) {
            jaCarregou.current = true;
            fetchUsinas(false, 0); // busca inicial sem filtros
        }
    }, [user?.empresa?.id, fetchUsinas]);

    const handleDelete = async (id) => {
        try {
            await del(`/hello-sol/usinas/${id}`);
            fetchUsinas(true, currentPage);
        } catch {
            // erro já tratado
        }
    };

    const confirmDelete = (id) => {
        confirmAlert({
            title: 'Confirmação de Exclusão',
            message: 'Tem certeza que deseja excluir esta usina?',
            buttons: [
                { label: 'Sim', onClick: () => handleDelete(id) },
                { label: 'Não' }
            ]
        });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFiltros(prev => ({
            ...prev,
            [name]: name === 'cnpj' ? somenteNumeros(value) : value
        }));
    };

    const handleSearch = (e) => {
        e.preventDefault();
        setCurrentPage(0);
        fetchUsinas(true, 0);
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 0 && newPage < totalPages) {
            setCurrentPage(newPage);
            fetchUsinas(true, newPage);
        }
    };

    const formatCnpj = (value) => {
        if (!value) return '';
        return value.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2}).*/, "$1.$2.$3/$4-$5");
    };

    return (
        <div className={styles_global.container}>
            <h2>Consultar Usinas</h2>

            <form className={styles.filterForm} onSubmit={handleSearch}>
                <div className={styles_content.formField}>
                    <label htmlFor="usina-cnpj">CNPJ</label>
                    <input
                        id="usina-cnpj"
                        type="text"
                        name="cnpj"
                        value={filtros.cnpj}
                        onChange={handleChange}
                        placeholder="Digite o CNPJ"
                        maxLength="18"
                    />
                </div>
                <div className={styles_content.formField}>
                    <label htmlFor="usina-nome">Nome</label>
                    <input
                        id="usina-nome"
                        type="text"
                        name="nome"
                        value={filtros.nome}
                        onChange={handleChange}
                        placeholder="Digite o Nome da Usina"
                        maxLength="100"
                    />
                </div>
                <div className={styles.botoesAbaixo}>
                    <button type="submit" disabled={loading}>
                        {loading ? 'Consultando...' : 'Consultar'}
                    </button>
                    <button type="button" onClick={() => navigate('/home/cadastrar-usina')}>
                        Nova Usina
                    </button>
                </div>
            </form>

            {loading ? (
                <p className={styles_global.loading}>Carregando...</p>
            ) : usinas.length > 0 ? (
                <div className={styles.tableContainer}>
                    <table className={styles.table}>
                        <thead>
                        <tr>
                            <th>CNPJ</th>
                            <th>Nome</th>
                            <th>Ações</th>
                        </tr>
                        </thead>
                        <tbody>
                        {usinas.map(usina => (
                            <tr key={usina.id}>
                                <td>{formatCnpj(usina.cnpj)}</td>
                                <td>{usina.nome}</td>
                                <td className={styles.actions}>
                                    <Link to={`/home/detalhar-usina/${usina.id}`} className={styles.iconButton}>
                                        <FaEye title="Visualizar" className={styles.icon} />
                                    </Link>
                                    <Link to={`/home/cadastrar-usina/${usina.id}`} className={styles.iconButton}>
                                        <FaEdit title="Editar" className={styles.icon} />
                                    </Link>
                                    <Link to="#" onClick={() => confirmDelete(usina.id)} className={styles.iconButton}>
                                        <FaTrashAlt title="Excluir" className={`${styles.icon} ${styles.delete}`} />
                                    </Link>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                    />
                </div>
            ) : (
                !loading && <p className={styles_global.noData}>Nenhuma usina encontrada.</p>
            )}
        </div>
    );
}

export default ListaUsinas;
